import { useContext, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { mockFn } from 'src/tools/fn.tools';
import { logUnableToProceed } from 'src/tools/log.tools';
import { ifTrue } from 'src/tools/logic.tools';
import { convertByEmailResultToShow } from 'src/tools/member.tools';
import { isEmail } from 'src/validators';
import { useDebouncedFn } from 'src/hooks/useDebouncedFn';

import { AutocompleteEmail, MemberResult } from 'src/models/autocomplete.model';
import { ButtonBaseType, ID, Result } from 'src/models';
import { getCustomIsLoading } from 'src/store/loading/loading.reducer';
import { getUserRoles } from 'src/store/team/team.getters';
import { InputContext } from 'src/contexts/input.context';
import { requestEmailAutocomplete } from 'src/store/autocomplete/autocomplete.actions';

import { BaseComponentProps } from 'src/components/Dropdown';
import { DropdownMenu } from 'src/components/Dropdown/DropdownMenu';
import { InputWithIcons } from 'src/components/Inputs';
import { MemberResultLine } from 'src/domains/team';

import { INVITE_BUTTON } from 'src/data/members.data';

export function EmailAutoComplete() {
  const {
    onChange,
    classNames,
    onDiscard = mockFn,
    isDisabled,
    isAutoFocus,
    customization = {},
    actions = {},
    placeholder,
  } = useContext(InputContext);

  const { resource_id, resource_type } = customization;

  const { onAddInputInvitedUser, onAddInputAddedUser } = actions;

  const [isOpen, setIsOpen] = useState(false);

  const [results, setResults] = useState<MemberResult[]>([]);

  const skipResponse = useRef(false);

  const dispatch = useDispatch();

  const isLoading = useSelector(getCustomIsLoading)('emailAutocomplete');

  const roles = useSelector(getUserRoles)(resource_type, resource_id);

  const initialRole = roles?.[0];

  const [inputValue, setInputValue] = useState('');

  const request = useDebouncedFn(sendRequest, 600);

  function handleOnItemClick(id: ID) {
    if (!initialRole || !onAddInputInvitedUser || !onAddInputAddedUser) {
      logUnableToProceed('EmailAutoComplete::handleOnSelect', {
        initialRole,
        onAddInputInvitedUser,
        onAddInputAddedUser,
      });
      return;
    }

    if (id === 'invite')
      onAddInputInvitedUser({ email: inputValue, role_id: initialRole.id, role_name: initialRole.name });
    else {
      const details = results.find(el => el.id === id);

      if (details) onAddInputAddedUser({ id, role_id: initialRole.id, role_name: initialRole.name, details });
    }
  }

  function handleOnChange(userInput: string) {
    setInputValue(userInput);
    onChange(userInput);

    if (userInput.length > 3) {
      request(userInput);
      skipResponse.current = false;
    } else if (!userInput) {
      setResults([]);
      skipResponse.current = true;
    }
  }

  function handleResults(response: Result<AutocompleteEmail[]>) {
    if (skipResponse.current || !response.isOK) {
      return;
    }

    if (!response.payload) setResults([]);
    else {
      let results: MemberResult[] = [];
      response.payload?.forEach(el => {
        results = [...results, convertByEmailResultToShow(el)];
      });

      // if input is a valid email, give user an option to invite
      if (isEmail(response.message)) results = [...results, INVITE_BUTTON];

      setResults(results);
      setIsOpen(true);
    }
  }

  function sendRequest(query: string) {
    if (!skipResponse.current && resource_id && resource_type) {
      dispatch(requestEmailAutocomplete({ resource_id, resource_type, query }, { callback: handleResults }));
    }
  }

  function handleOnRequestOpenClose(newState: boolean) {
    setIsOpen(newState);

    if (!newState) onDiscard();
  }

  function handleOnDiscard() {
    handleOnRequestOpenClose(false);
  }

  function renderDropdownLabel(item: ButtonBaseType) {
    const member = results.find(el => el.id === item.id);

    if (member) return <MemberResultLine member={member} />;

    return <></>;
  }

  function renderBaseComponent({ onClick, onFocus, onBlur }: BaseComponentProps) {
    const iconId = isOpen ? 'chevron-up' : 'chevron-down';

    return (
      <InputWithIcons
        className={classNames?.input}
        value={inputValue}
        onFocus={onFocus}
        onBlur={onBlur}
        isDisabled={isDisabled}
        autoFocus={isAutoFocus}
        rightIconId={ifTrue(results.length, iconId)}
        onClick={onClick}
        isLoading={isLoading}
        onDiscard={handleOnDiscard}
        onChange={handleOnChange}
        placeholder={placeholder}
      />
    );
  }

  return (
    <DropdownMenu
      classNames={classNames}
      items={results}
      onItemClick={handleOnItemClick}
      renderBaseComponent={renderBaseComponent}
      isOpen={isOpen}
      onRequestOpenClose={handleOnRequestOpenClose}
      renderItemLabel={renderDropdownLabel}
      dropdownMinWidth='container-width'
    />
  );
}
